/**
 * Team / Draw Store
 *
 * Manages draw-phase and scoring-phase team state.
 * Used by draw management and live scoring pages.
 */

import { create } from 'zustand';
import { httpClient } from '../api/http-client';

// --- Types ---

export interface TeamEntry {
  id: string;
  team_number: number;
  header_name: string;
  heeler_name: string;
  header_registration_id: string;
  heeler_registration_id: string;
  status: string;
  [key: string]: unknown;
}

export interface TeamScore {
  round_number: number;
  time_seconds: number | null;
  is_no_time: boolean;
  penalty_seconds: number;
}

export interface DrawStatusInfo {
  event_id: string;
  status: string;
  total_teams: number;
  locked: boolean;
  executed_at: string | null;
}

export type TeamSortField = 'team_number' | 'header_name' | 'heeler_name' | 'status';
export type SortDirection = 'asc' | 'desc';

interface TeamFilters {
  search: string;
  round: number | null;
  status: string | null;
}

interface TeamStoreState {
  // Data
  eventId: string | null;
  teams: TeamEntry[];
  drawStatus: DrawStatusInfo | null;
  teamScores: Record<string, TeamScore[]>;

  // Filters & sorting
  filters: TeamFilters;
  sortField: TeamSortField;
  sortDirection: SortDirection;

  // Loading states (per-operation)
  loadingTeams: boolean;
  loadingStatus: boolean;
  loadingScores: Record<string, boolean>;
  error: string | null;
}

interface TeamStoreActions {
  // Data loading
  setEventId: (eventId: string) => void;
  fetchTeams: () => Promise<void>;
  fetchDrawStatus: () => Promise<void>;
  fetchTeamScores: (teamId: string) => Promise<void>;

  // Filters & sorting
  setSearch: (search: string) => void;
  setRoundFilter: (round: number | null) => void;
  setStatusFilter: (status: string | null) => void;
  setSort: (field: TeamSortField, direction?: SortDirection) => void;

  // Computed
  filteredTeams: () => TeamEntry[];

  // Reset
  resetTeamStore: () => void;
}

type TeamStore = TeamStoreState & TeamStoreActions;

const initialFilters: TeamFilters = {
  search: '',
  round: null,
  status: null,
};

const initialState: TeamStoreState = {
  eventId: null,
  teams: [],
  drawStatus: null,
  teamScores: {},
  filters: { ...initialFilters },
  sortField: 'team_number',
  sortDirection: 'asc',
  loadingTeams: false,
  loadingStatus: false,
  loadingScores: {},
  error: null,
};

export const useTeamStore = create<TeamStore>()((set, get) => ({
  ...initialState,

  // --- Event ---
  setEventId: (eventId) => {
    set({ eventId, teams: [], drawStatus: null, teamScores: {}, error: null });
  },

  // --- Fetch Teams ---
  fetchTeams: async () => {
    const { eventId, filters } = get();
    if (!eventId) return;
    set({ loadingTeams: true, error: null });
    try {
      const qs = new URLSearchParams();
      if (filters.search) qs.set('search', filters.search);
      if (filters.round) qs.set('round', String(filters.round));
      const query = qs.toString() ? `?${qs}` : '';
      const teams = await httpClient.get<TeamEntry[]>(
        `/draw-ops/event/${eventId}/teams${query}`,
      );
      set({ teams });
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : 'Failed to fetch teams',
      });
    } finally {
      set({ loadingTeams: false });
    }
  },

  // --- Fetch Draw Status ---
  fetchDrawStatus: async () => {
    const { eventId } = get();
    if (!eventId) return;
    set({ loadingStatus: true, error: null });
    try {
      const drawStatus = await httpClient.get<DrawStatusInfo>(
        `/draw-ops/event/${eventId}/status`,
      );
      set({ drawStatus });
    } catch (err) {
      set({
        error:
          err instanceof Error ? err.message : 'Failed to fetch draw status',
      });
    } finally {
      set({ loadingStatus: false });
    }
  },

  // --- Fetch Team Scores ---
  fetchTeamScores: async (teamId) => {
    const { eventId } = get();
    if (!eventId) return;
    set((state) => ({
      loadingScores: { ...state.loadingScores, [teamId]: true },
    }));
    try {
      const scores = await httpClient.get<TeamScore[]>(
        `/scoring-ops/event/${eventId}/team/${teamId}/scores`,
      );
      set((state) => ({
        teamScores: { ...state.teamScores, [teamId]: scores },
        loadingScores: { ...state.loadingScores, [teamId]: false },
      }));
    } catch (err) {
      set((state) => ({
        error:
          err instanceof Error ? err.message : 'Failed to fetch team scores',
        loadingScores: { ...state.loadingScores, [teamId]: false },
      }));
    }
  },

  // --- Filters ---
  setSearch: (search) =>
    set((state) => ({ filters: { ...state.filters, search } })),

  setRoundFilter: (round) =>
    set((state) => ({ filters: { ...state.filters, round } })),

  setStatusFilter: (status) =>
    set((state) => ({ filters: { ...state.filters, status } })),

  // --- Sorting ---
  setSort: (field, direction) => {
    const { sortField, sortDirection } = get();
    if (field === sortField && !direction) {
      set({ sortDirection: sortDirection === 'asc' ? 'desc' : 'asc' });
    } else {
      set({ sortField: field, sortDirection: direction || 'asc' });
    }
  },

  // --- Computed: Filtered + Sorted Teams ---
  filteredTeams: () => {
    const { teams, filters, sortField, sortDirection } = get();
    let result = [...teams];

    // Search filter
    if (filters.search) {
      const q = filters.search.toLowerCase();
      result = result.filter(
        (t) =>
          t.header_name.toLowerCase().includes(q) ||
          t.heeler_name.toLowerCase().includes(q) ||
          String(t.team_number).includes(q),
      );
    }

    // Status filter
    if (filters.status) {
      result = result.filter((t) => t.status === filters.status);
    }

    // Sort
    result.sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      const aStr = String(aVal || '');
      const bStr = String(bVal || '');
      return sortDirection === 'asc'
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    });

    return result;
  },

  // --- Reset ---
  resetTeamStore: () => set({ ...initialState }),
}));

// Selector hooks
export const useTeams = () => useTeamStore((s) => s.teams);
export const useDrawStatus = () => useTeamStore((s) => s.drawStatus);
export const useTeamFilters = () => useTeamStore((s) => s.filters);
export const useTeamLoading = () =>
  useTeamStore((s) => ({
    loadingTeams: s.loadingTeams,
    loadingStatus: s.loadingStatus,
  }));
